/**
 * World-space labels above every place you can interact with.
 *
 * The V1 build's only signal that something was interactable was a bar at the
 * bottom of the screen (VISUAL_AUDIT V-08). A player could stand in the middle
 * of the neighbourhood with no idea which of the buildings around them did
 * anything. These are billboards that face the camera, fade in with proximity,
 * and brighten when the player is actually in range — so the world itself
 * tells you where to go.
 */
import * as THREE from 'three';
import { LOCATIONS, type WorldLocation } from '../../sim/data/world.js';

export interface Signpost {
  sprite: THREE.Sprite;
  location: WorldLocation;
  /** Height above ground the label floats at. */
  y: number;
}

export interface SignpostRig {
  group: THREE.Group;
  update(playerX: number, playerZ: number, nearId: string | null, jobLocations: Set<string>): void;
}

const LABEL_KINDS: Record<string, { text: string; colour: string; icon: string }> = {
  home: { text: 'Home', colour: '#ffd23f', icon: '⌂' },
  store: { text: 'Corner Store', colour: '#ff9d6b', icon: '🛒' },
  toolshop: { text: 'Tool Depot', colour: '#ffd23f', icon: '🔧' },
  bank: { text: 'Bank', colour: '#7fc4ff', icon: '🏦' },
  depot: { text: 'Depot — Shifts & Gigs', colour: '#b6c2d1', icon: '📦' },
  park: { text: 'Maple Green', colour: '#8fe0a0', icon: '🌳' },
};

export function createSignposts(): SignpostRig {
  const group = new THREE.Group();
  const posts: Signpost[] = [];

  for (const location of LOCATIONS) {
    const kind = LABEL_KINDS[location.kind];
    // Customer houses get their street address rather than a category.
    const text = kind ? kind.text : location.name;
    const colour = kind ? kind.colour : '#ffffff';
    const icon = kind ? kind.icon : '🏡';

    const sprite = makeLabel(`${icon}  ${text}`, colour);
    const y = location.height > 0 ? location.height + 5.5 : 7;
    sprite.position.set(location.x + location.entryX * 0.35, y, location.z + location.entryZ * 0.35);
    sprite.userData.baseY = y;
    group.add(sprite);
    posts.push({ sprite, location, y });
  }

  function update(
    playerX: number,
    playerZ: number,
    nearId: string | null,
    jobLocations: Set<string>,
  ): void {
    for (const post of posts) {
      const ex = post.location.x + post.location.entryX;
      const ez = post.location.z + post.location.entryZ;
      const distance = Math.hypot(playerX - ex, playerZ - ez);

      const isCustomer = post.location.kind === 'customer';
      const hasJob = jobLocations.has(post.location.id);
      // Customer houses only announce themselves when you have work there;
      // otherwise a street of twelve labels is noise, not information.
      const relevant = !isCustomer || hasJob;

      // Fade in from 55 m, full by 34 m, then hide again very close so the
      // label never covers the doorway you are trying to look at.
      let opacity = 0;
      if (relevant) {
        opacity = THREE.MathUtils.clamp((55 - distance) / 21, 0, 1);
        if (distance < 7) opacity *= THREE.MathUtils.clamp((distance - 3.5) / 3.5, 0, 1);
      }

      const material = post.sprite.material as THREE.SpriteMaterial;
      material.opacity += (opacity - material.opacity) * 0.14;
      post.sprite.visible = material.opacity > 0.01;

      const active = nearId === post.location.id;
      const scale = (active ? 1.16 : 1) * (hasJob ? 1.1 : 1);
      const width = (post.sprite.userData.width as number) * scale;
      post.sprite.scale.set(width, (post.sprite.userData.height as number) * scale, 1);
      // Job markers bob; plain labels sit still.
      post.sprite.position.y = post.y + (hasJob ? Math.sin(performance.now() / 520) * 0.35 : 0);
    }
  }

  return { group, update };
}

/**
 * Render a label to a canvas and hand it back as a sprite.
 *
 * Sprites rather than HTML overlays: they occlude correctly against the world,
 * scale with distance for free, and do not need a projection pass every frame.
 */
export function makeLabel(text: string, colour: string): THREE.Sprite {
  const padding = 26;
  const fontSize = 46;
  const measure = document.createElement('canvas').getContext('2d')!;
  measure.font = `600 ${fontSize}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
  const textWidth = measure.measureText(text).width;

  const width = Math.ceil(textWidth + padding * 2);
  const height = fontSize + padding * 1.5;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;

  // Panel
  const radius = height / 2;
  ctx.fillStyle = 'rgba(14, 18, 26, 0.82)';
  roundRect(ctx, 0, 0, width, height, radius);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.16)';
  ctx.lineWidth = 3;
  roundRect(ctx, 1.5, 1.5, width - 3, height - 3, radius - 1.5);
  ctx.stroke();

  ctx.fillStyle = colour;
  ctx.font = `600 ${fontSize}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, width / 2, height / 2 + 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.anisotropy = 8;

  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    opacity: 0,
    depthTest: true,
    depthWrite: false,
    fog: false,
  }));
  // Scale so the label is a consistent physical size in the world.
  const worldHeight = 1.5;
  const worldWidth = worldHeight * (width / height);
  sprite.scale.set(worldWidth, worldHeight, 1);
  sprite.userData.width = worldWidth;
  sprite.userData.height = worldHeight;
  sprite.renderOrder = 10;
  return sprite;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
